import { useTranslation } from 'react-i18next'

import { GooglePlayIcon } from '@/components/GooglePlayIcon/GooglePlayIcon'
import { appLinks } from '@/config/links'

const HERO_BG = '/hero-bg.webp'
const FEATURES_BG = '/features-bg.webp'
const PHONE_SCREEN = '/phone-mockup.jpg'

const FEATURE_ICONS = [
  <svg key="0" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="3" y="4" width="18" height="16" rx="3" stroke="#1A56FF" strokeWidth="1.8" />
    <path d="M7 9h10M7 13h6M7 17h4" stroke="#1A56FF" strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
  <svg key="1" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 4a6 6 0 016 6v3.2l1.6 2.1a1 1 0 01-.8 1.6H5.2a1 1 0 01-.8-1.6L6 13.2V10a6 6 0 016-6z"
      stroke="#F7931A"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M10 20a2 2 0 004 0" stroke="#F7931A" strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
  <svg key="2" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="6" cy="12" r="2.2" stroke="#00C896" strokeWidth="1.8" />
    <circle cx="18" cy="7" r="2.2" stroke="#00C896" strokeWidth="1.8" />
    <circle cx="18" cy="17" r="2.2" stroke="#00C896" strokeWidth="1.8" />
    <path d="M8 11l7-3M8 13l7 3" stroke="#00C896" strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
  <svg key="3" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 3l7 3v5c0 4.5-2.7 8.6-7 10-4.3-1.4-7-5.5-7-10V6l7-3z" stroke="#8B5CF6" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M9.5 12.5l1.8 1.8 3.6-4" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg key="4" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M7 7h10M7 12h10M7 17h6" stroke="#EC4899" strokeWidth="1.8" strokeLinecap="round" />
    <rect x="4" y="4" width="16" height="16" rx="3" stroke="#EC4899" strokeWidth="1.8" />
  </svg>,
  <svg key="5" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M6 5h12a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2z" stroke="#0EA5E9" strokeWidth="1.8" />
    <path d="M8 9h8M8 13h8M8 17h5" stroke="#0EA5E9" strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
]

type FeatureCopy = { title: string; desc: string; color: string; bg: string }
type StepCopy = { step: string; title: string; desc: string; color: string }

/** 安卓直屏旗舰示意：窄边框、居中挖孔前摄、音量条 + 电源键（不映射真实机型） */
function HeroPreview({ alt }: { alt: string }) {
  return (
    <div className="relative w-full max-w-[400px]">
      <div
        className="relative mx-auto p-[8px]"
        style={{
          width: 'min(100%, 325px)',
          borderRadius: '36px',
          background: 'linear-gradient(155deg, #2F3238 0%, #1A1C20 40%, #0E0F12 100%)',
          boxShadow:
            '0 28px 56px rgba(13, 27, 62, 0.26), inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 4px rgba(0,0,0,0.35)',
        }}
      >
        {/* 左侧一体音量条（常见安卓布局） */}
        <div
          className="absolute -left-[2px] top-[128px] h-[52px] w-[3px] rounded-l-sm"
          style={{ background: 'linear-gradient(180deg, #4A4D54, #25262B)' }}
        />
        {/* 右侧电源键 */}
        <div
          className="absolute -right-[2px] top-[188px] h-[72px] w-[3px] rounded-r-sm"
          style={{ background: 'linear-gradient(180deg, #4A4D54, #25262B)' }}
        />

        <div
          className="relative overflow-hidden bg-black"
          style={{
            borderRadius: '30px',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04), inset 0 2px 12px rgba(0,0,0,0.4)',
          }}
        >
          <div className="relative w-full" style={{ aspectRatio: '9 / 20' }}>
            <img
              src={PHONE_SCREEN}
              alt={alt}
              className="pointer-events-none absolute inset-0 block h-full w-full object-cover"
              decoding="async"
              fetchPriority="high"
            />
            {/* 听筒微缝 */}
            <div
              className="pointer-events-none absolute left-1/2 top-[8px] z-10 h-[3px] w-14 -translate-x-1/2 rounded-full"
              style={{ background: 'rgba(30,32,38,0.95)', boxShadow: '0 0.5px 0 rgba(255,255,255,0.06) inset' }}
            />
            {/* 居中挖孔前摄 */}
            <div
              className="pointer-events-none absolute left-1/2 top-[18px] z-20 h-[11px] w-[11px] -translate-x-1/2 rounded-full"
              style={{
                background: 'radial-gradient(circle at 35% 35%, #1e2936, #050608 70%)',
                boxShadow:
                  '0 0 0 2px #0a0b0d, 0 0 0 3px rgba(255,255,255,0.06), inset 0 0.5px 1px rgba(255,255,255,0.12)',
              }}
            />
            {/* 底部手势指示条（叠在画面底部） */}
            <div className="pointer-events-none absolute bottom-2.5 left-1/2 z-10 -translate-x-1/2">
              <div
                className="h-[3px] w-[118px] rounded-full"
                style={{ background: 'rgba(255,255,255,0.22)', boxShadow: '0 0 0 0.5px rgba(255,255,255,0.05)' }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function OperationsPanel({ labels }: { labels: Record<string, string> }) {
  return (
    <div
      className="w-full max-w-[420px] rounded-[32px] border border-[#1A56FF]/12 p-6"
      style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F7FAFF 100%)', boxShadow: '0 18px 48px rgba(26, 86, 255, 0.12)' }}
    >
      <div className="mb-5 flex items-center justify-between">
        <div>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '21px', fontWeight: 800, color: '#0D1B3E' }}>{labels.title}</div>
          <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#6B7A99' }}>{labels.subtitle}</div>
        </div>
        <div
          className="inline-flex items-center gap-2 rounded-full px-3 py-1.5"
          style={{ background: 'rgba(0, 200, 150, 0.1)', border: '1px solid rgba(0, 200, 150, 0.2)' }}
        >
          <div className="h-2 w-2 rounded-full" style={{ background: '#00C896' }} />
          <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '11px', fontWeight: 700, color: '#00C896' }}>{labels.status}</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: labels.online, value: labels.onlineValue, accent: '#1A56FF', bg: 'rgba(26, 86, 255, 0.08)' },
          { label: labels.alerts, value: labels.alertsValue, accent: '#F7931A', bg: 'rgba(247, 147, 26, 0.1)' },
          { label: labels.sync, value: labels.syncValue, accent: '#00C896', bg: 'rgba(0, 200, 150, 0.1)' },
        ].map((item) => (
          <div key={item.label} className="rounded-2xl p-4" style={{ background: item.bg }}>
            <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '11px', color: '#6B7A99', marginBottom: '6px' }}>{item.label}</div>
            <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '16px', fontWeight: 700, color: item.accent }}>{item.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {[
          { name: labels.nameOne, detail: labels.rowOne, width: '99%', color: '#00C896' },
          { name: labels.nameTwo, detail: labels.rowTwo, width: '92%', color: '#1A56FF' },
          { name: labels.nameThree, detail: labels.rowThree, width: '88%', color: '#F7931A' },
        ].map((row) => (
          <div key={row.name} className="rounded-2xl border border-[#1A56FF]/8 bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: 700, color: '#0D1B3E' }}>{row.name}</div>
                <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '11px', color: '#6B7A99' }}>{row.detail}</div>
              </div>
              <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '13px', fontWeight: 700, color: row.color }}>{row.width}</div>
            </div>
            <div style={{ height: '4px', background: 'rgba(13,27,62,0.06)', borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{ width: row.width, height: '100%', background: `linear-gradient(90deg, ${row.color}, ${row.color}bb)` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function HomePage() {
  const { t } = useTranslation()
  const features = t('home.features', { returnObjects: true }) as FeatureCopy[]
  const steps = t('home.how.steps', { returnObjects: true }) as StepCopy[]

  const panelLabels = {
    title: t('home.how.panel.title'),
    subtitle: t('home.how.panel.subtitle'),
    status: t('home.how.panel.status'),
    online: t('home.how.panel.online'),
    onlineValue: t('home.how.panel.onlineValue'),
    alerts: t('home.how.panel.alerts'),
    alertsValue: t('home.how.panel.alertsValue'),
    sync: t('home.how.panel.sync'),
    syncValue: t('home.how.panel.syncValue'),
    nameOne: t('home.how.panel.nameOne'),
    nameTwo: t('home.how.panel.nameTwo'),
    nameThree: t('home.how.panel.nameThree'),
    rowOne: t('home.how.panel.rowOne'),
    rowTwo: t('home.how.panel.rowTwo'),
    rowThree: t('home.how.panel.rowThree'),
  }

  return (
    <div style={{ background: '#F0F4FF', minHeight: '100vh' }}>
      <section
        className="relative overflow-hidden pt-16"
        style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            opacity: 0.42,
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(240,244,255,0.92) 0%, rgba(240,244,255,0.7) 50%, rgba(220,232,255,0.76) 100%)',
          }}
        />

        <div className="relative mx-auto w-full max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div className="animate-slide-in-left">
              <div
                className="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2"
                style={{
                  background: 'rgba(26, 86, 255, 0.08)',
                  border: '1px solid rgba(26, 86, 255, 0.15)',
                }}
              >
                <div className="h-2 w-2 rounded-full" style={{ background: '#00C896', boxShadow: '0 0 6px #00C896' }} />
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: 600, color: '#1A56FF' }}>
                  {t('home.hero.badge')}
                </span>
              </div>

              <h1
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 900,
                  fontSize: 'clamp(36px, 5vw, 64px)',
                  color: '#0D1B3E',
                  lineHeight: 1.1,
                  letterSpacing: '-0.04em',
                  marginBottom: '24px',
                }}
              >
                {t('home.hero.title1')}
                <br />
                <span
                  style={{
                    background: 'linear-gradient(110deg, #1A56FF 20%, #00C8FF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {t('home.hero.title2')}
                </span>
              </h1>

              <p
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 'clamp(15px, 1.5vw, 18px)',
                  color: '#6B7A99',
                  lineHeight: 1.7,
                  marginBottom: '36px',
                  maxWidth: '560px',
                }}
              >
                {t('home.hero.subtitle')}
              </p>

              <div className="mb-10 flex flex-col gap-4 sm:flex-row">
                <a
                  href={appLinks.googlePlay}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 rounded-2xl px-6 py-4 transition-all duration-200"
                  style={{
                    background: '#0D1B3E',
                    color: 'white',
                    textDecoration: 'none',
                    boxShadow: '0 8px 32px rgba(13, 27, 62, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(13, 27, 62, 0.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(13, 27, 62, 0.3)'
                  }}
                >
                  <GooglePlayIcon size={28} />
                  <div>
                    <div style={{ fontSize: '10px', opacity: 0.6, lineHeight: 1, fontFamily: 'DM Sans' }}>{t('common.getItOn')}</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, fontFamily: 'DM Sans', lineHeight: 1.2 }}>{t('common.googlePlay')}</div>
                  </div>
                </a>

                <a
                  href="#features"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 transition-all duration-200"
                  style={{
                    background: 'rgba(26, 86, 255, 0.08)',
                    border: '1.5px solid rgba(26, 86, 255, 0.2)',
                    color: '#1A56FF',
                    textDecoration: 'none',
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: 600,
                    fontSize: '16px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(26, 86, 255, 0.12)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(26, 86, 255, 0.08)'
                  }}
                >
                  {t('home.hero.learnMore')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="#1A56FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              </div>

            </div>

            <div className="flex animate-slide-in-right justify-center lg:justify-end">
              <HeroPreview alt={t('home.hero.phoneAlt')} />
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="flex flex-col items-center gap-1" style={{ animation: 'float-slow 2s ease-in-out infinite' }}>
            <div style={{ width: '1px', height: '32px', background: 'linear-gradient(180deg, transparent, rgba(26,86,255,0.4))' }} />
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden>
              <path d="M1 1l5 5 5-5" stroke="rgba(26,86,255,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </section>

      <section
        id="features"
        className="relative overflow-hidden py-24"
        style={{
          backgroundImage: `url(${FEATURES_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <div
              className="mb-4 inline-flex items-center gap-2 rounded-full px-4 py-2"
              style={{ background: 'rgba(26, 86, 255, 0.08)', border: '1px solid rgba(26, 86, 255, 0.12)' }}
            >
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: 600, color: '#1A56FF' }}>
                {t('home.featuresSection.badge')}
              </span>
            </div>
            <h2 className="minego-section-title mb-4" style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}>
              {t('home.featuresSection.title1')}
              <br />
              <span style={{ color: '#1A56FF' }}>{t('home.featuresSection.title2')}</span>
            </h2>
            <p
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '16px',
                color: '#6B7A99',
                maxWidth: '560px',
                margin: '0 auto',
                lineHeight: 1.7,
              }}
            >
              {t('home.featuresSection.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <div
                key={`${feature.title}-${index}`}
                className="minego-card p-6 transition-all duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)'
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(26, 86, 255, 0.12)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow =
                    '0 4px 24px rgba(26, 86, 255, 0.06), 0 1px 0 rgba(255,255,255,0.8) inset'
                }}
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: feature.bg }}>
                  {FEATURE_ICONS[index]}
                </div>
                <h3
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 700,
                    fontSize: '17px',
                    color: '#0D1B3E',
                    marginBottom: '8px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {feature.title}
                </h3>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#6B7A99', lineHeight: 1.65 }}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ background: 'white' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
            <div className="flex justify-center">
              <OperationsPanel labels={panelLabels} />
            </div>

            <div>
              <div
                className="mb-4 inline-flex items-center gap-2 rounded-full px-4 py-2"
                style={{ background: 'rgba(26, 86, 255, 0.08)', border: '1px solid rgba(26, 86, 255, 0.12)' }}
              >
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: 600, color: '#1A56FF' }}>
                  {t('home.how.badge')}
                </span>
              </div>
              <h2 className="minego-section-title mb-3" style={{ fontSize: 'clamp(26px, 3.5vw, 40px)' }}>
                {t('home.how.title1')}
                <br />
                <span style={{ color: '#F7931A' }}>{t('home.how.title2')}</span>
              </h2>
              <p
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '15px',
                  color: '#6B7A99',
                  marginBottom: '40px',
                  lineHeight: 1.7,
                }}
              >
                {t('home.how.subtitle')}
              </p>

              <div className="space-y-6">
                {steps.map((step) => (
                  <div key={step.step} className="flex items-start gap-5">
                    <div
                      className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl"
                      style={{
                        background: `linear-gradient(135deg, ${step.color}15, ${step.color}25)`,
                        border: `1.5px solid ${step.color}30`,
                      }}
                    >
                      <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: '15px', color: step.color }}>
                        {step.step}
                      </span>
                    </div>
                    <div>
                      <h3
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 700,
                          fontSize: '17px',
                          color: '#0D1B3E',
                          marginBottom: '6px',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {step.title}
                      </h3>
                      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#6B7A99', lineHeight: 1.65 }}>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24" style={{ background: '#F0F4FF' }}>
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div
            className="relative overflow-hidden rounded-[36px] px-6 py-12 text-center sm:px-12"
            style={{ background: 'linear-gradient(135deg, #0D1B3E 0%, #1A2E5A 100%)', boxShadow: '0 20px 60px rgba(13, 27, 62, 0.22)' }}
          >
            <div
              className="absolute top-0 right-0 h-56 w-56 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(26,86,255,0.16) 0%, transparent 70%)', filter: 'blur(32px)' }}
            />
            <div className="relative">
              <div
                className="mb-4 inline-flex items-center gap-2 rounded-full px-4 py-2"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: 600, color: '#8DB5FF' }}>{t('home.cta.badge')}</span>
              </div>
              <h2
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 900,
                  fontSize: 'clamp(28px, 4vw, 44px)',
                  color: 'white',
                  letterSpacing: '-0.04em',
                  marginBottom: '16px',
                }}
              >
                {t('home.cta.title1')}
                <br />
                <span style={{ color: '#8DB5FF' }}>{t('home.cta.title2')}</span>
              </h2>
              <p
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '15px',
                  color: 'rgba(255,255,255,0.62)',
                  maxWidth: '620px',
                  margin: '0 auto 28px',
                  lineHeight: 1.7,
                }}
              >
                {t('home.cta.subtitle')}
              </p>

              <a
                href={appLinks.googlePlay}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-2xl px-6 py-4 transition-all duration-200"
                style={{
                  background: 'white',
                  color: '#0D1B3E',
                  textDecoration: 'none',
                  boxShadow: '0 8px 24px rgba(255,255,255,0.16)',
                }}
              >
                <GooglePlayIcon size={24} />
                <div className="text-left">
                  <div style={{ fontSize: '9px', opacity: 0.55, lineHeight: 1, fontFamily: 'DM Sans' }}>{t('common.getItOn')}</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, fontFamily: 'DM Sans', lineHeight: 1.2 }}>{t('common.googlePlay')}</div>
                </div>
              </a>

              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginTop: '16px' }}>
                {t('home.cta.footnote')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
